import moment from "moment-timezone";
import request from "superagent";

import { getErrorMessage } from "../utils/errors";
import { nameFromEmail } from "../utils/string";
import { FirebaseAuth } from "../utils/firebase";
import { cognitoSignOut } from "@src/appV2/Auth/cognito/cognitoAuth";
import { getAuthHeader } from "../utils/superagent";
import { clearSessionStorage } from "../utils/clearSessionStorage";
import { identifyDatadogRumUser } from "../utils/datadog";
import { showErrorToast, showWarningToast } from "@src/appV2/lib/Notifications";

import {
  LOGIN_REQUESTED,
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  REGISTER_FAILURE,
  UPDATE_PROFILE,
  LOGOUT,
  LOCATION_AGENTS_SUCCESS,
  UPDATE_FLAG,
  REACTIVATE_ACCOUNT,
  ADMIN_PROFILE,
  ADMIN_PROFILE_SUCCESS,
  FIREBASE_LOGIN_SUCCESS,
  FIREBASE_LOGOUT,
  UPDATE_DRAWER,
  ADMIN_SESSION_TYPE,
  FIREBASE_TOKEN_READY,
  OPEN_SHIFT_CONFIRMATION,
  SET_BOOKING_ROLLOUT_MSAS,
  HCPS_WITH_PENDING_DOCS,
  LD_PROFILE_IDENTIFIED,
} from "@src/appV2/Auth/cognito/sessionActionTypes";

export {
  LOGIN_REQUESTED,
  LOGIN_FAILURE,
  LOGIN_SUCCESS,
  REGISTER_FAILURE,
  UPDATE_PROFILE,
  LOGOUT,
  LOCATION_AGENTS_SUCCESS,
  UPDATE_FLAG,
  REACTIVATE_ACCOUNT,
  ADMIN_PROFILE,
  ADMIN_PROFILE_SUCCESS,
  FIREBASE_LOGIN_SUCCESS,
  FIREBASE_LOGOUT,
  UPDATE_DRAWER,
  ADMIN_SESSION_TYPE,
  FIREBASE_TOKEN_READY,
  OPEN_SHIFT_CONFIRMATION,
  SET_BOOKING_ROLLOUT_MSAS,
  HCPS_WITH_PENDING_DOCS,
  LD_PROFILE_IDENTIFIED,
};

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initialState: any = {
  userId: "",
  adminProfile: null,
  loadingAdminProfile: false,
  isLoggingIn: false,
  type: "",
  profile: null,
  loginMessage: "",
  admin: false,
  superadmin: false,
  drawer: {},
  flags: {},
  user: null,
  firebaseToken: null,
  ready: false,
  ldProfileIsIdentified: false,
};
/* eslint no-case-declarations: 0 */
/* eslint-env es6 */
// eslint-disable-next-line @typescript-eslint/default-param-last
export default (state = initialState, action) => {
  switch (action.type) {
    case LOGIN_REQUESTED:
      return {
        ...state,
        isLoggingIn: true,
        loginMessage: "",
      };

    case LOGIN_FAILURE:
      return {
        ...state,
        isLoggingIn: false,
        loginMessage: action.message || getErrorMessage(action.data),
      };

    case LOGIN_SUCCESS:
      const user = action.data;

      const enabledStatuses = [
        "Instant Payouts Enabled",
        "Standard Payouts Enabled",
        "No Documents Required",
      ];
      if (
        user.agent &&
        user.agent.employmentStatus !== "W2" &&
        user.agent.paymentAccountInfo &&
        !enabledStatuses.includes(user.agent.paymentAccountInfo.status)
      ) {
        showWarningToast(
          "This HCP cannot be assigned to a shift until they are enabled for Stripe"
        );
      }
      if (user.agent && user.agent.employmentStatus !== "W2" && !user.agent.paymentAccountInfo) {
        showWarningToast(
          "This HCP cannot be assigned to a shift until they are enabled for Stripe"
        );
      }

      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const session: any = {
        userId: user._id,
        // eslint-disable-next-line no-nested-ternary
        adminUserId: state.adminUserId ? state.adminUserId : user.admin ? user._id : "",
        // eslint-disable-next-line no-nested-ternary
        adminProfile: state.adminProfile
          ? state.adminProfile
          : user.admin
            ? user.employee
            : undefined,
        type: user.type,
        profile: user.facility ||
          user.agent ||
          (user.employee && {
            ...user.employee,
            ...(user.employee.name ? {} : { name: nameFromEmail(user.email) }),
          }) || { name: nameFromEmail(user.email) },
        isLoggingIn: false,
        flags: user.flags,
        admin: Boolean(state.admin || user.admin), // maintain admin when pseudo
        superadmin: user.superadmin || state.superadmin,
        tz: user.tmz,
        sendBirdAccessToken: user.sendBirdAccessToken,
      };

      // Update roles for admin profile without logging out
      if (session.profile?.roles) {
        session.adminProfile.roles = session.profile.roles;
      }

      // save immutable fields to local storage
      saveToLocalStorage(session);

      moment.tz.setDefault(user.tmz);

      if (!session.admin && session.type === "FACILITY") {
        const { user: userInfo } = state;

        updateAnalyticsUserProperties({
          userId: userInfo._id,
          name: userInfo.name,
          userInfo,
          ...(userInfo?.intercomHash && { intercomHash: user.intercomHash }),
        });
        /* @ts-expect-error FIXME this wrong type */
        window.analytics?.group(session.userId, {
          name: session.profile.name,
          groupType: "Workplace",
        });
      } else {
        const { user: userInfo } = state;
        updateAnalyticsUserProperties({
          userId: session.adminUserId || session.userId,
          name: session.adminUserId
            ? session.adminProfile.name || nameFromEmail(session.adminProfile.email)
            : session.profile.name,
          userInfo: session.adminProfile || session.user,
          ...(userInfo?.intercomHash && { intercomHash: user.intercomHash }),
        });
      }
      const newState = {
        ...state,
        ...session,
        isLoggedOut: false,
      };
      identifyDatadogRumUser(newState);

      return newState;

    case FIREBASE_LOGIN_SUCCESS:
      return {
        ...state,
        ...action.data,
        isLoggedOut: false,
      };

    case FIREBASE_LOGOUT:
      if (state.type === "FACILITY") {
        saveToLocalStorage(null);
        return initialState;
      } else {
        return {
          ...state,
        };
      }

    case REGISTER_FAILURE:
      return {
        ...state,
        isLoggingIn: false,
        loginMessage: getErrorMessage(action.data),
      };

    case LOGOUT:
      clearSessionStorage();

      // Only set isLoggedOut for explicit user logouts (via the logout() action
      // creator which passes isExplicit: true). LOGOUT is also dispatched during
      // session init (initCognitoSession, onLoggedOut) when no valid session
      // exists — setting isLoggedOut there would prevent PrivateRoute from
      // capturing the requestedUrl on subsequent navigation to protected pages.
      return { ...initialState, isLoggedOut: action.data?.isExplicit === true };

    case UPDATE_FLAG:
      const updatedState = {
        ...state,
        flags: {
          ...state.flags,
          [action.data.key]: action.data.value,
        },
      };

      saveToLocalStorage(updatedState);
      return updatedState;

    case REACTIVATE_ACCOUNT:
      return {
        ...state,
        profile: {
          ...state.profile,
          quit: action.data,
        },
      };
    case ADMIN_PROFILE:
      return {
        ...state,
        loadingAdminProfile: true,
      };

    case UPDATE_PROFILE:
      return {
        ...state,
        profile: action.data,
      };

    case ADMIN_PROFILE_SUCCESS: {
      const { user: dataUser } = action.data;
      const newSession = {
        ...state,
        adminProfile: dataUser.employee,
        ...(state.type === "ADMIN" ? { profile: dataUser.employee } : {}),
        loadingAdminProfile: false,
      };
      saveToLocalStorage(newSession);
      return newSession;
    }
    case UPDATE_DRAWER: {
      return {
        ...state,
        drawer: action.data,
      };
    }
    case FIREBASE_TOKEN_READY: {
      return {
        ...state,
        ready: action.data.ready,
      };
    }

    case LD_PROFILE_IDENTIFIED: {
      return {
        ...state,
        ldProfileIsIdentified: true,
      };
    }

    default:
      return state;
  }
};

export const logout = () => {
  return async (dispatch) => {
    // Clear redirectURL only on explicit user logout, not in the LOGOUT reducer,
    // because LOGOUT is also dispatched during session init (initCognitoSession)
    // when no valid session exists — which would clear the URL that PrivateRoute
    // just stored before the login form even appears.
    localStorage.removeItem("redirectURL");
    // Dispatch LOGOUT synchronously first so callers that don't await
    // (e.g. myAccount/utils.ts followed by window.location.reload()) get
    // immediate Redux state and localStorage cleanup.
    dispatch({ type: LOGOUT, data: { isExplicit: true } });
    // Await cognitoSignOut so Amplify clears its in-memory session cache.
    // Without this, navigating to a magic link after logout still shows
    // "already signed in" because fetchAuthSession() finds the cached session.
    // Callers that await dispatch(logout()) — e.g. LogoutConfirmationDialog —
    // will wait for this to complete before proceeding.
    await cognitoSignOut();
    // Fire-and-forget: Firebase signOut triggers onIdTokenChanged which
    // would dispatch LOGOUT again; we don't need to wait for it.
    void FirebaseAuth.signOut();
  };
};

// syntactic sugar for get
export const pseudo = (data) => {
  return (dispatch) => {
    return dispatch(get({ ...data }));
  };
};

// User's session, alternatively takes explicit
export const get = (sessionData) => {
  return async (dispatch) => {
    // TODO dispatch creation requested
    const { userId, admin, superadmin } = sessionData || {};
    if (!userId) {
      return;
    }

    // numberOfSession to keep track of number of refresh pages for each facility
    const SESSION_KEY = `numberOfSession_${userId}`;
    /* @ts-expect-error FIXME this wrong type */
    localStorage.setItem(SESSION_KEY, +(localStorage.getItem(SESSION_KEY) ?? 0) + 1);
    localStorage.setItem("selectedUser", userId);
    dispatch({ type: LOGIN_REQUESTED });
    return request
      .get(`${global.api}/user/get/${userId}`)
      .set(await getAuthHeader())
      .then((res) => {
        // add in admin
        if (admin) {
          res.body.admin = admin;
          res.body.superadmin = superadmin;
        }

        dispatch({
          type: LOGIN_SUCCESS,
          data: { ...res.body },
        });
      })
      .catch((error) => showErrorToast(getErrorMessage(error)));
  };
};

export const getAdminProfile = () => {
  return async (dispatch) => {
    dispatch({ type: ADMIN_PROFILE });
    const response = await request.get(`${global.api}/user/getByEmail`).set(await getAuthHeader());
    dispatch({ type: ADMIN_PROFILE_SUCCESS, data: { user: response.body } });
  };
};

export const updateFlag = (userId, flagInfo) => async (dispatch) => {
  dispatch({ type: UPDATE_FLAG, data: flagInfo });

  await request
    .post(`${global.api}/user/flags`)
    .set(await getAuthHeader())
    .send({ flagInfo, userId });
};

// load state from localStorage
export const loadSession = () => {
  try {
    // "state" in this case is just the session
    const serializedState = localStorage.getItem("state");

    if (serializedState === null || serializedState === "null") {
      return initialState; // reducer will return Redux state, as localstorage is null.
    }
    const session = JSON.parse(serializedState);

    // Workaround to avoid issues with new roles DB field. If user was
    // loggedIn and is an admin, we set the loadingAdminProfile since
    // we are going to fetch the new profile
    const hasSession = session.userId;
    const isAdmin = session.type === "ADMIN";
    const { admin } = session;
    const { adminProfile } = session;
    const { profile } = session;
    if (
      (hasSession && isAdmin && !profile.roles) ||
      (hasSession && !isAdmin && admin && !adminProfile)
    ) {
      session.loadingAdminProfile = true;
    }

    moment.tz.setDefault(session.tz);

    const firebaseToken = localStorage.getItem("AUTH_TOKEN");
    session.firebaseToken = firebaseToken;

    return session;
    // FIXME: Automatically disabled during ESLint config update
  } catch {
    return initialState;
  }
};

const saveToLocalStorage = (state) => {
  try {
    // remove from from state
    // delete state.user.from;
    const serializedState = JSON.stringify(state);
    localStorage.setItem("state", serializedState);
    // FIXME: Automatically disabled during ESLint config update
  } catch {
    // TODO handle error
  }
};

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const updateAnalyticsUserProperties = ({ userId, name, userInfo, intercomHash }) => {
  // @ts-expect-error FIXME this wrong type
  window.analytics?.identify(
    userId,
    {},
    {
      integrations: intercomHash?.length
        ? {
            Intercom: {
              user_hash: intercomHash,
            },
          }
        : {},
    }
  );
};

export const updateDrawer = (drawerLink) => {
  return (dispatch) => {
    dispatch({
      type: UPDATE_DRAWER,
      data: drawerLink,
    });
  };
};
